import React, { useCallback, useMemo, useState } from 'react';
import { Input } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { extractScriptMetadata } from '@kemu-io/kemu-core/widgets/script/index.js';
import { ScriptWidgetState } from '@kemu-io/kemu-core/widgets/script/types.js';
import { pasteFromClipboardAction } from '../reducers/clipboardReducers';
import styles from './ScriptCreationAssistant.module.css';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import platformApi from '@src/api/platform/platformApi';
import { useTranslation } from '@hooks/index';
import RoundedModal from '@components/roundedModal/roundedModal';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import { showGlobalNotification, showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';
import { decodeServerErrorInfo } from '@src/app/reducers/widget/utils';
import { SUBSCRIPTION_LIMIT_REACHED } from '@common/constants';
import { getWidgetCanvasInfo } from '@src/app/recipe/utils';

/**
 * Removes markdown code block wrappers from a string.
 * @param script - The string with potential markdown wrappers.
 * @returns The script string without markdown wrappers.
 */
const removeMarkdownWrappers = (script: string): string => {
  // Remove starting ```optional_language and closing ```
  const regex = /^```(?:[a-zA-Z0-9]+)?\n?([\s\S]*?)\n?```$/;
  const match = script.match(regex);
  if (match && match[1]) {
    return match[1].trim();
  }
  // If no match, or if the capturing group is empty, return the original string trimmed
  return script.trim();
};

type Props = {
  /** Whether the modal is visible */
  visible: boolean;
  /** Callback when modal is cancelled */
  onCancel: () => void;
  /** The port value to use as context for script generation */
  portValue: string | number | boolean | Record<string, unknown> | undefined;
  /** The type of the port value */
  portValueType: 'json' | 'text';
  /** The ID of the widget whose port is being inspected */
  widgetId: string;
  /** The recipe pool ID */
  recipeId: string;
}

const SCRIPT_TEMPLATE = {
  '{#}id-001': {
    'id': '{#}id-001',
    'canvas': {
      'position': {
        'x': 953,
        'y': 210
      }
    },
    'children': [],
    'state': {
      'pauseExecution': false,
      'autoPauseOnError': true,
      'metadata': {
        'name': 'My Script',
        'description': 'This is a test script.'
      },
      'pages': {
        'main': {
          'code': '--code here--',
          'language': 'javascript',
          'name': 'My First Script'
        }
      }
    },
    'type': 'script'
  }
};

/**
 * Modal component for generating scripts based on port values using AI assistance
 * @param visible - Whether the modal is visible
 * @param onCancel - Callback when modal is cancelled
 * @param portValue - The port value to use as context for script generation
 */
const ScriptCreationAssistant = (props: Props) => {
  const { visible, onCancel, portValue, portValueType, widgetId, recipeId } = props;
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const currentUser = useSelector(selectSignedUserProfile);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const dispatch = useDispatch();
  const t = useTranslation();
  const wpT = useMemo(() => t.withBaseKey('LogicMapper.WidgetPortDebugPanel.ScriptCreationAssistant'), [t]);

  /** Default widget width when dimensions are not available */
  const DEFAULT_WIDGET_WIDTH = 100;
  /** Horizontal gap between source widget and generated script */
  const HORIZONTAL_GAP = 50;

  /**
   * Handles the generated script by creating a script widget positioned to the right of the source widget
   * @param generatedScript - The script code generated by the AI
   */
  const generateScript = useCallback((generatedScript: string) => {
    const cleanedScript = removeMarkdownWrappers(generatedScript);
    const templateCopy = JSON.parse(JSON.stringify(SCRIPT_TEMPLATE));
    templateCopy['{#}id-001'].state.pages.main.code = cleanedScript;

    // process metadata created by the AI (if any)
    const metadata = extractScriptMetadata(cleanedScript);
    if (metadata) {
      (templateCopy['{#}id-001'].state as ScriptWidgetState).metadata = {
        ...(templateCopy['{#}id-001'].state as ScriptWidgetState).metadata,
        ...metadata,
      };
    }

    if (currentRecipe.poolId) {
      // Calculate position to the right of the source widget
      let targetPosition = { x: 0, y: 0 };
      if (widgetId && recipeId) {
        try {
          const sourceWidgetCanvas = getWidgetCanvasInfo(recipeId, DEFAULT_THING_ID, widgetId);
          const sourceWidth = sourceWidgetCanvas.dimensions?.width || DEFAULT_WIDGET_WIDTH;
          targetPosition = {
            x: sourceWidgetCanvas.position.x + sourceWidth + HORIZONTAL_GAP,
            y: sourceWidgetCanvas.position.y,
          };
        } catch (error) {
          console.warn('Could not get source widget canvas info, using default position', error);
        }
      }

      dispatch(pasteFromClipboardAction({
        recipeId: currentRecipe.poolId,
        thingId: DEFAULT_THING_ID,
        mapData: JSON.stringify(templateCopy),
        mouseLocation: targetPosition,
      }));

      dispatch(showGlobalNotification({
        type: 'success',
        code: 'script-creation',
        message: wpT('ScriptGenerated'),
      }));
    }
  }, [wpT, currentRecipe, dispatch, widgetId, recipeId]);


  /**
   * Handles the script generation request
   */
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      dispatch(showGlobalNotification({
        type: 'warning',
        code: 'script-creation',
        title: wpT('GenerationError'),
        message: wpT('EmptyPromptWarning'),
      }));
      return;
    }

    if (!currentUser.profile?.id) {
      return;
    }

    setIsGenerating(true);
    let accumulatedResponse = '';

    try {
      await platformApi.valueProcessorGen({
        prompt,
        // inputValue: JSON.stringify(portValue, null, 2),
        inputValue: portValue,
        inputValueType: portValueType,
        // apiKey: currentUser.apiKey,
        userId: currentUser.profile.id,
        onSession: () => {
          // We don't need to track session for this simple use case
        },
        onChunk: (chunk) => {
          accumulatedResponse += chunk;
        },
      });

      // Call the generateScript function with the accumulated response
      generateScript(accumulatedResponse);

      // Close the modal after successful generation
      onCancel();
      setPrompt('');

    } catch (error) {
      console.error('Error generating script:', error);
      const { errorCode, message: errMessage } = decodeServerErrorInfo(error);

      if(errorCode === SUBSCRIPTION_LIMIT_REACHED){
        dispatch(showPremiumUpgradeModal(true));
      }
      dispatch(showGlobalNotification({
        type: 'error',
        code: 'script-creation',
        title: wpT('GenerationError'),
        message: errMessage,
      }));
    } finally {
      setIsGenerating(false);
    }
  }, [prompt, portValue, portValueType, wpT, onCancel, generateScript]);

  /**
   * Handles modal cancellation
   */
  const handleCancel = useCallback(() => {
    if (!isGenerating) {
      setPrompt('');
      onCancel();
    }
  }, [isGenerating, onCancel]);

  /**
   * Handles Enter key press in the textarea
   */
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  }, [handleGenerate, isGenerating]);

  return (
    <RoundedModal
      title={wpT('Title')}
      visible={visible}
      onCancel={handleCancel}
      className={styles.ScriptCreationModalRoot}
      okBtnLabel={wpT('GenerateBtn')}
      cancelBtnLabel={wpT('CancelBtn')}
      onOk={handleGenerate}
      centered
      loadingOkBtn={isGenerating}
      disableOkButton={!prompt.trim() || isGenerating}
      disableCancelButton={isGenerating}
      closeOnMaskClick={false}
      closable={!isGenerating}
    >
      <div className={styles.Content}>
        <div className={styles.Description}>
          {wpT('Description')}
        </div>

        <Input.TextArea
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={wpT('PromptPlaceholder')}
          disabled={isGenerating}
          onKeyDown={handleKeyPress}
          className={styles.PromptInput}
        />
      </div>
    </RoundedModal>
  );
};

export default ScriptCreationAssistant;
